import React from "react";
import { GraphView } from "./views/GraphView";
import useSummaryPage from "./hooks/useSummaryPage";
import { CohortAnalysis } from "./views/CohortAnalysis";

export default function SummaryV2() {
  const {
    tab,
    setTab,
    dataset,
    setDataset,
    dataType,
    setDataType,
    rangeKey,
    setRangeKey,
    aggKey,
    setAggKey,

    customFrom,
    setCustomFrom,
    customTo,
    setCustomTo,

    graphLoading,
    graphError,
    graphSeries,

    cumulative,
    setCumulative,

    reportLoading,
    reportError,
    activeReportType,
    fetchReportInvites,
    fetchReportCanceledSubs,
    fetchReportFreeTrialUsers,

    previewType,
    previewRows,
    previewCols,
    previewPage,
    setPreviewPage,
    previewPageSize,
    previewTotalPages,
    previewPageRows,
    handleReportClick,
  } = useSummaryPage();

  const isFreeTrialDownload =
    previewType === "freeTrialUsers" && previewRows.length > 0;
  const isInvitesDownload = previewType === "invites" && previewRows.length > 0;
  const isCanceledDownload =
    previewType === "canceledSubs" && previewRows.length > 0;

  return (
    <div style={styles.page}>
      <style>{`
        @keyframes summarySpin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
      <div style={styles.header}>
        <div>
          <div style={styles.title}>Summary </div>
          <div style={styles.subtitle}>Aggregated metrics over time</div>
        </div>

        <div style={styles.tabs}></div>
      </div>

      <div style={styles.content}>
        <GraphView
          dataset={dataset}
          dataType={dataType}
          rangeKey={rangeKey}
          aggKey={aggKey}
          onChangeDataset={setDataset}
          onChangeType={setDataType}
          onChangeRange={setRangeKey}
          onChangeAgg={setAggKey}
          series={graphSeries}
          loading={graphLoading}
          error={graphError}
          customFrom={customFrom}
          setCustomFrom={setCustomFrom}
          customTo={customTo}
          setCustomTo={setCustomTo}
          cumulative={cumulative}
          onChangeCumulative={setCumulative}
        />
      </div>

      <div style={styles.reportRow}>
        <button
          type="button"
          style={isFreeTrialDownload ? styles.btnDownload : styles.btn}
          disabled={reportLoading}
          onClick={() =>
            handleReportClick(
              "freeTrialUsers",
              fetchReportFreeTrialUsers,
              "free_trial_users",
            )
          }
        >
          {isFreeTrialDownload
            ? "Download Free Trial Users"
            : "Preview Free Trial Users"}
        </button>
        {activeReportType === "freeTrialUsers" ? (
          <span style={styles.spinner} />
        ) : null}

        <button
          type="button"
          style={isInvitesDownload ? styles.btnDownload : styles.btn}
          disabled={reportLoading}
          onClick={() =>
            handleReportClick("invites", fetchReportInvites, "invites")
          }
        >
          {isInvitesDownload ? "Download Invites" : "Preview Invites"}
        </button>
        {activeReportType === "invites" ? (
          <span style={styles.spinner} />
        ) : null}

        <button
          type="button"
          style={isCanceledDownload ? styles.btnDownload : styles.btn}
          disabled={reportLoading}
          onClick={() =>
            handleReportClick(
              "canceledSubs",
              fetchReportCanceledSubs,
              "canceled_subscriptions",
            )
          }
        >
          {isCanceledDownload
            ? "Download Canceled Subscriptions"
            : "Preview Canceled Subscriptions"}
        </button>
        {activeReportType === "canceledSubs" ? (
          <span style={styles.spinner} />
        ) : null}

        {reportError ? (
          <div style={{ fontSize: 12, color: "#c00" }}>
            Report failed
            {reportError?.message ? `: ${reportError.message}` : ""}
          </div>
        ) : null}
      </div>

      <div style={styles.previewCard}>
        <div style={styles.previewHeader}>
          <div style={styles.previewTitle}>Report Preview</div>
          <div style={styles.previewMeta}>
            {previewRows.length} row{previewRows.length === 1 ? "" : "s"}
          </div>
        </div>
        {previewRows.length === 0 ? (
          <div style={styles.previewEmpty}>
            Click a Preview button to load data.
          </div>
        ) : (
          <div style={styles.previewTableWrap}>
            <table style={styles.previewTable}>
              <thead>
                <tr>
                  {previewCols.map((c) => (
                    <th key={c} style={styles.previewTh}>
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {previewPageRows.map((r, i) => (
                  <tr key={i}>
                    {previewCols.map((c) => (
                      <td key={c} style={styles.previewTd}>
                        {String(r?.[c] ?? "")}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {previewRows.length > 0 ? (
          <div style={styles.previewPager}>
            <button
              type="button"
              style={styles.pagerBtn}
              disabled={previewPage <= 1}
              onClick={() => setPreviewPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </button>
            <div style={styles.pagerMeta}>
              Page {previewPage} of {previewTotalPages}
            </div>
            <button
              type="button"
              style={styles.pagerBtn}
              disabled={previewPage >= previewTotalPages}
              onClick={() =>
                setPreviewPage((p) => Math.min(previewTotalPages, p + 1))
              }
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      <div>
        <CohortAnalysis />
      </div>
    </div>
  );
}

const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    padding: 16,
    gap: 12,
    background: "#fafafa",
  },
  header: {
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: 12,
  },
  title: { fontSize: 18, fontWeight: 600 },
  subtitle: { fontSize: 12, color: "#666" },
  tabs: { display: "flex", gap: 10 },
  content: {
    flex: 1,
    minHeight: 0,
    overflow: "auto",
    display: "grid",
    gap: 12,
  },
  reportRow: {
    display: "flex",
    gap: 10,
    flexWrap: "wrap",
    alignItems: "center",
  },
  btn: {
    border: "1px solid #ddd",
    borderRadius: 10,
    padding: "8px 10px",
    background: "#fff",
    cursor: "pointer",
    fontSize: 12,
  },
  btnDownload: {
    border: "1px solid #1e7f41",
    borderRadius: 10,
    padding: "8px 10px",
    background: "#28a745",
    color: "#fff",
    cursor: "pointer",
    fontSize: 12,
  },
  spinner: {
    width: 12,
    height: 12,
    borderRadius: "50%",
    border: "2px solid #d0d0d0",
    borderTopColor: "#666",
    animation: "summarySpin 0.8s linear infinite",
    marginLeft: 6,
  },
  previewCard: {
    border: "1px solid #e5e5e5",
    borderRadius: 12,
    background: "#fff",
    padding: 12,
    display: "grid",
    gap: 8,
  },
  previewHeader: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 10,
  },
  previewTitle: {
    fontSize: 13,
    fontWeight: 600,
  },
  previewMeta: {
    fontSize: 11,
    color: "#666",
  },
  previewEmpty: {
    fontSize: 12,
    color: "#777",
  },
  previewTableWrap: {
    overflow: "auto",
    maxHeight: 220,
    border: "1px solid #eee",
    borderRadius: 8,
  },
  previewTable: {
    width: "100%",
    borderCollapse: "collapse",
    fontSize: 12,
  },
  previewTh: {
    position: "sticky",
    top: 0,
    background: "#fafafa",
    textAlign: "left",
    padding: "6px 8px",
    borderBottom: "1px solid #eee",
    whiteSpace: "nowrap",
  },
  previewTd: {
    padding: "6px 8px",
    borderBottom: "1px solid #f0f0f0",
    whiteSpace: "nowrap",
  },
  previewPager: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 10,
    fontSize: 12,
  },
  pagerBtn: {
    border: "1px solid #ddd",
    borderRadius: 8,
    padding: "4px 8px",
    background: "#fff",
    cursor: "pointer",
    fontSize: 12,
  },
  pagerMeta: {
    color: "#666",
  },
};
